import { Injectable, NgZone } from '@angular/core';
import { IContext, IContextContactInfo } from '@ext-interfaces/context.model';
import { BehaviorSubject, Observable } from 'rxjs';
declare const Office: any;

@Injectable({
    providedIn: 'root'
})
export class MicrosoftContextService implements IContext {
    private readonly _contactInfo = new BehaviorSubject<IContextContactInfo | undefined>(undefined);
    public readonly $contactInfo: Observable<IContextContactInfo> = this._contactInfo.asObservable();

    constructor(private ngZone: NgZone) {
        if (!this.isOfficeLoaded()) {
            console.log('MicrosoftContextService running in local dev mode with mocked contact info.');
        }

        const contactInfo = this.getContactInfo();
        this._contactInfo.next(contactInfo);

        this.addMailboxListener();
    }

    private isOfficeVersionSupported() {
        return !!Office.context.requirements.isSetSupported('Mailbox', '1.5');
    }

    /**
     * If the user is reading an email, extracts contact name, and email.
     */
    private getContactInfo() {
        // We disable the connect tab if Outlook Mailbox version is not >= 1.5. This logic would be pointless to run since Office.context.mailbox.addHandlerAsync() within change-notifier.service.ts doesn't run.
        // See addHandlerAsync() here:  https://docs.microsoft.com/en-us/office/dev/add-ins/reference/objectmodel/requirement-set-1.5/office.context.mailbox#methods

        if (!this.isOfficeLoaded()) {
            return {
                name: 'Jane Doe',
                email: 'janedoecalendarhero@calendarhero.com'
            };
        }

        if (!this.isOfficeVersionSupported()) {
            return undefined;
        }

        const isInEmailMode = !!Office.context.mailbox.item.from && !!Office.context.mailbox.item.from.displayName;
        if (!isInEmailMode) {
            return undefined;
        }

        const contactName = `${Office.context.mailbox.item.from.displayName}`;
        const contactEmail = `${Office.context.mailbox.item.from.emailAddress}`;

        return { name: contactName, email: contactEmail };
    }

    private addMailboxListener() {
        // Office.context.mailbox.addHandlerAsync() is only supported in >= 1.5 Outlook mailbox https://docs.microsoft.com/en-us/office/dev/add-ins/reference/objectmodel/requirement-set-1.5/office.context.mailbox#methods
        // We also do not want to call Outlook methods if Office isn't loaded i.e  local dev
        if (!this.isOfficeLoaded() || !this.isOfficeVersionSupported()) {
            return;
        }

        // In the Outlook plugin, partially because this app runs within the outlook add-in shell (fancy iframe), it is recommended by Outlook documentation to handle it this way.
        // This was also similar logic leveraged from the old Outlook plugin.
        this.ngZone.runOutsideAngular(() => {
            Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
                this.ngZone.run(() => {
                    try {
                        const contactInfo = this.getContactInfo();
                        if (!contactInfo) {
                            return;
                        }

                        this._contactInfo.next(contactInfo);
                    } catch (e) {
                        console.log('Error while receiving mailbox event', e);
                    }
                });
            });
        });
    }

    private isOfficeLoaded() {
        return window.hasOwnProperty('Office');
    }
}
